import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, Plus, CalendarRange, Layers } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';
import MenuDayDialog from './menu/MenuDayDialog';
import MenuRangeDialog from './menu/MenuRangeDialog';
import MenuBatchesPanel from './menu/MenuBatchesPanel';
import MarketingRecipientsPanel from './menu/MarketingRecipientsPanel';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Format a Date to YYYY-MM-DD using LOCAL parts (never toISOString).
const toDateOnly = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${day}`;
};

const todayStr = toDateOnly(new Date());

const MenuCalendar = () => {
  const [anchor, setAnchor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [menus, setMenus] = useState({});       // { 'YYYY-MM-DD': menuDoc }
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeDate, setActiveDate] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);

  // Build the 6x7 grid of dates for the displayed month.
  const grid = useMemo(() => {
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - firstOfMonth.getDay()); // back to Sunday
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [anchor]);

  const loadMenus = useCallback(async () => {
    setLoading(true);
    try {
      const start = toDateOnly(grid[0]);
      const end = toDateOnly(grid[grid.length - 1]);
      const data = await adminService.getMenuDays(start, end);
      const map = {};
      data.forEach((m) => { map[m.date] = m; });
      setMenus(map);
    } catch {
      toast.error('Failed to load menus');
    } finally {
      setLoading(false);
    }
  }, [grid]);

  useEffect(() => { loadMenus(); }, [loadMenus]);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminService.getProducts({ status: 'available' });
        setProducts(data);
      } catch (err) {
        // Products are optional for the grid view; the dialog will show an empty
        // list. Log so the failure is diagnosable rather than silently swallowed.
        console.error('MenuCalendar: failed to load products for menu builder', err);
      }
    })();
  }, []);

  const openDay = async (d) => {
    const dateStr = toDateOnly(d);
    setActiveDate(dateStr);
    const summary = menus[dateStr];
    if (summary) {
      // Fetch hydrated detail (with items) before opening.
      try {
        const detail = await adminService.getMenuDay(dateStr);
        setActiveMenu(detail);
      } catch {
        setActiveMenu(summary);
      }
    } else {
      setActiveMenu(null);
    }
    setDialogOpen(true);
  };

  const monthLabel = anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const goPrev = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const goNext = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
  const goToday = () => { const n = new Date(); setAnchor(new Date(n.getFullYear(), n.getMonth(), 1)); };

  const publishedCount = Object.values(menus).filter((m) => m.published).length;

  return (
    <div className="space-y-6" data-testid="menu-calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-amber-600" />
            Menu Calendar
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Schedule special menus on specific calendar dates. Clients can book ahead for
            published dates — additive menus add items to the normal catalog, exclusive menus
            replace it for that day.
          </p>
        </div>
        {publishedCount > 0 && (
          <Badge className="bg-green-100 text-green-800 self-start" data-testid="menu-published-count">
            {publishedCount} published this view
          </Badge>
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setBatchesOpen(true)} data-testid="menu-batches-btn">
            <Layers className="h-4 w-4 mr-2" /> Batches
          </Button>
          <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => setRangeOpen(true)} data-testid="menu-range-btn">
            <CalendarRange className="h-4 w-4 mr-2" /> Create Range Menu
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goPrev} data-testid="menu-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={goNext} data-testid="menu-next-month"><ChevronRight className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={goToday}>Today</Button>
            </div>
            <h3 className="text-lg font-semibold text-gray-900" data-testid="menu-month-label">{monthLabel}</h3>
            <div className="w-[120px] flex justify-end">
              {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            </div>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-xs font-medium text-gray-500 py-1">{w}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d) => {
              const dateStr = toDateOnly(d);
              const inMonth = d.getMonth() === anchor.getMonth();
              const menu = menus[dateStr];
              const isToday = dateStr === todayStr;
              return (
                <button
                  key={dateStr}
                  onClick={() => openDay(d)}
                  data-testid={`menu-cell-${dateStr}`}
                  className={`group relative min-h-[92px] rounded-lg border p-2 text-left transition-colors
                    ${inMonth ? 'bg-white hover:bg-amber-50' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}
                    ${isToday ? 'ring-2 ring-amber-400' : 'border-gray-200'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${inMonth ? 'text-gray-900' : 'text-gray-400'}`}>{d.getDate()}</span>
                    {!menu && (
                      <Plus className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  {menu && (
                    <div className="mt-1 space-y-1">
                      <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight">
                        {menu.title || (menu.mode === 'exclusive' ? 'Exclusive menu' : 'Menu')}
                      </p>
                      <div className="flex flex-wrap items-center gap-1">
                        {menu.published ? (
                          <Badge className="bg-green-100 text-green-800 text-[10px] px-1 py-0">Live</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1 py-0">Draft</Badge>
                        )}
                        <Badge className={`text-[10px] px-1 py-0 ${menu.mode === 'exclusive' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                          {menu.mode === 'exclusive' ? 'Excl' : 'Add'}
                        </Badge>
                        <span className="text-[10px] text-gray-500">{menu.item_count} item{menu.item_count === 1 ? '' : 's'}</span>
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {dialogOpen && (
        <MenuDayDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          date={activeDate}
          existingMenu={activeMenu}
          products={products}
          onSaved={loadMenus}
        />
      )}

      {rangeOpen && (
        <MenuRangeDialog
          open={rangeOpen}
          onClose={() => setRangeOpen(false)}
          products={products}
          onApplied={loadMenus}
        />
      )}

      {batchesOpen && (
        <MenuBatchesPanel
          open={batchesOpen}
          onClose={() => setBatchesOpen(false)}
          onChanged={loadMenus}
          products={products}
        />
      )}

      <MarketingRecipientsPanel />
    </div>
  );
};

export default MenuCalendar;
